import { useId, useState } from 'react';

import { AnnotationsPanel } from './AnnotationsPanel';
import { Evidence } from './Evidence';
import type { Prediction, ReviewFieldName } from './schema';
import styles from './review.module.css';
import { Sheet } from '../ui/Sheet';

export interface EvidenceSheetProps {
  readonly position: number;
  readonly prediction: Prediction | undefined;
  readonly noteId: string;
  readonly fieldLabel: string;
  readonly whyFlagged: string;
  readonly onClose: () => void;
  readonly onShowFullImage: () => void;
  readonly annotations: readonly { readonly clientId: string; readonly text: string; readonly lineKey: string | null; readonly fieldName: ReviewFieldName | null }[];
  readonly editable: boolean;
  readonly onAddAnnotation: (text: string) => void;
  readonly onRemoveAnnotation: (clientId: string) => void;
}

/**
 * "Ver evidencia" on a flagged line (redesign §Revisar nota): the cropped region, why it was
 * flagged, and the full photo/annotation actions - reusing `Evidence.tsx`,
 * `review/FullImageOverlay.tsx` (via the caller's `onShowFullImage`), and `AnnotationsPanel`
 * rather than duplicating any of their photo-region or annotation logic.
 */
export function EvidenceSheet({
  position,
  prediction,
  noteId,
  fieldLabel,
  whyFlagged,
  onClose,
  onShowFullImage,
  annotations,
  editable,
  onAddAnnotation,
  onRemoveAnnotation,
}: EvidenceSheetProps) {
  const [writingNote, setWritingNote] = useState(false);
  const inputId = useId();
  const [draftText, setDraftText] = useState('');

  return (
    <Sheet title={`Evidencia · fila ${String(position)}`} onClose={onClose}>
      <p className={styles.evidenceSheetReason}>{whyFlagged}</p>
      <Evidence noteId={noteId} prediction={prediction} fieldLabel={fieldLabel} onShowFullImage={onShowFullImage} />

      {annotations.length > 0 ? (
        <AnnotationsPanel
          title="Notas de esta fila"
          annotations={annotations}
          fieldChoices={[]}
          editable={editable}
          onAdd={(text) => {
            onAddAnnotation(text);
          }}
          onRemove={onRemoveAnnotation}
        />
      ) : null}

      {editable ? (
        writingNote ? (
          <div className={styles.annotationForm}>
            <label className={styles.label} htmlFor={inputId}>
              Escribe una nota
            </label>
            <textarea
              id={inputId}
              className={styles.textarea}
              value={draftText}
              maxLength={500}
              onChange={(event) => {
                setDraftText(event.target.value);
              }}
            />
            <button
              type="button"
              className={styles.linkButton}
              disabled={draftText.trim() === ''}
              onClick={() => {
                onAddAnnotation(draftText);
                setDraftText('');
                setWritingNote(false);
              }}
            >
              Guardar nota
            </button>
          </div>
        ) : (
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => {
              setWritingNote(true);
            }}
          >
            Escribir una nota
          </button>
        )
      ) : null}
    </Sheet>
  );
}
